import React, { useEffect, useMemo, useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes, css } from 'styled-components'
import Tooltip from '@mui/material/Tooltip'
import LogoShort from 'assets/images/bp-logo-orange.png'
import { openWidget } from 'ducks/guruWidget/actions'
import { loadRecommendationsRoutine } from 'ducks/guruRecommendations/actions'
import { selectBadgeStateFor, selectRecommendationsFor } from 'ducks/guruRecommendations/selectors'
import { useGuruVisibility } from './hooks/useGuruVisibility'
import { useGuruContext } from './hooks/useGuruContext'
import { useGuruKeyboardShortcuts } from './hooks/useGuruKeyboardShortcuts'
import { readGuruPreferences } from './guruPreferences'
import { useFloatingSlot, FLOATING_PRIORITY } from 'components/atoms/floatingStack'

const ONBOARDING_KEY = 'guru.launcher.onboarded.v1'

const criticalPulse = keyframes`
  0%   { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18), 0 0 0 0 rgba(229, 57, 53, 0.55); }
  70%  { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18), 0 0 0 12px rgba(229, 57, 53, 0); }
  100% { box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18), 0 0 0 0 rgba(229, 57, 53, 0); }
`

const Fab = styled.button`
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid ${({ theme, $critical }) => $critical ? '#e53935' : theme.colors.lightGrey};
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  cursor: pointer;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: transform 0.15s ease, border-color 0.2s ease;

  ${({ $critical }) => $critical && css`
    animation: ${criticalPulse} 2.2s ease-out infinite;
  `}

  &:hover { transform: translateY(-1px); }
  &:focus-visible {
    outline: none;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18), 0 0 0 3px rgba(247, 124, 0, 0.35);
  }

  @media (max-width: 768px) {
    width: 46px;
    height: 46px;
    right: 16px;
    bottom: var(--bp-slot, var(--bp-float-1));
    /* Below every modal/drawer backdrop in the app (lowest is 999) so any
       overlay covers the stack without having to register itself. */
    z-index: 900;
  }
`

const Avatar = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`

const BADGE_COLOURS = {
  critical: '#e53935',
  medium:   '#9e9e9e',
}

const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${({ $tier }) => BADGE_COLOURS[$tier] || '#9e9e9e'};
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff;
`

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform || '')
const shortcutHint = isMac ? '⌘K' : 'Ctrl+K'

const GuruLauncher = () => {
  const dispatch = useDispatch()
  const ctx = useGuruContext()
  const visible = useGuruVisibility()
  const badgeSelector = useMemo(
    () => selectBadgeStateFor(ctx.contextType, ctx.contextId),
    [ctx.contextType, ctx.contextId],
  )
  const recommendationsSelector = useMemo(
    () => selectRecommendationsFor(ctx.contextType, ctx.contextId),
    [ctx.contextType, ctx.contextId],
  )
  const badge = useSelector(badgeSelector)
  const slice = useSelector(recommendationsSelector)
  const [hintOpen, setHintOpen] = useState(false)
  const [hovered, setHovered] = useState(false)
  const slot = useFloatingSlot('guru-launcher', FLOATING_PRIORITY.launcher, visible)

  useGuruKeyboardShortcuts()

  // Eager-load recommendations so the badge shows up before the user opens
  // the widget. Re-fires when context (BP or sub-surface) changes, and when
  // visibility flips on. Guarded by fetchedAt so we don't spam on every
  // re-render — Suggestions view has its own 5min Redux cache via fetchedAt.
  useEffect(() => {
    if (!visible || !ctx.contextType) return
    if (ctx.contextType !== 'user' && !ctx.contextId) return
    const fresh = slice.fetchedAt && Date.now() - slice.fetchedAt < 5 * 60 * 1000
    if (fresh || slice.loading) return
    dispatch(loadRecommendationsRoutine({
      contextType: ctx.contextType,
      contextId: ctx.contextId,
      surface: 'widget',
      subSurface: ctx.subSurface,
      entityType: ctx.entityType,
      entityId: ctx.entityId,
    }))
  }, [visible, ctx.contextType, ctx.contextId, ctx.subSurface, ctx.entityType, ctx.entityId, slice.fetchedAt, slice.loading, dispatch])

  useEffect(() => {
    if (!visible) return undefined
    try {
      if (window.localStorage.getItem(ONBOARDING_KEY)) return undefined
    } catch (_) {
      return undefined
    }

    const show = setTimeout(() => setHintOpen(true), 1500)
    const hide = setTimeout(() => {
      setHintOpen(false)
      try { window.localStorage.setItem(ONBOARDING_KEY, '1') } catch (_) {}
    }, 8500)

    return () => {
      clearTimeout(show)
      clearTimeout(hide)
    }
  }, [visible])

  if (!visible || slot.hidden) return null

  const dismissHint = () => {
    setHintOpen(false)
    try { window.localStorage.setItem(ONBOARDING_KEY, '1') } catch (_) {}
  }

  const tooltipTitle = hintOpen
    ? `New: Ask Guru anything about your Building Passports — press ${shortcutHint}`
    : `Ask Guru  ·  ${shortcutHint}`

  const node = (
    <Tooltip
      title={tooltipTitle}
      placement="left"
      arrow
      open={hintOpen || hovered}
      onOpen={() => setHovered(true)}
      onClose={() => setHovered(false)}
    >
      <Fab
        style={slot.style}
        onClick={() => { dismissHint(); dispatch(openWidget(ctx)) }}
        aria-label="Ask Guru"
        $critical={badge.tier === 'critical'}
      >
        <Avatar src={LogoShort} alt="Guru" />
        {badge.tier && badge.count > 0 && (badge.tier === 'critical' || readGuruPreferences().showMediumBadge) && (
          <Badge $tier={badge.tier}>{badge.count}</Badge>
        )}
      </Fab>
    </Tooltip>
  )

  return ReactDOM.createPortal(node, document.body)
}

export default GuruLauncher
